import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Spinner, Alert, Badge,
  Row,
  Col,
  InputGroup,
  Card
} from 'react-bootstrap';
import { FaTrashAlt, FaInfoCircle, FaEdit, FaEye, FaEyeSlash, FaSearch, FaUsers } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Swal from 'sweetalert2';

// get driver api from apiClient
import { driversAPI } from '../../api/apiClient';

// for pagination
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import '../../assets/css/Driver.css';

function Drivers() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = parseInt(searchParams.get('page')) || 1;
  const searchFromUrl = searchParams.get('search') || '';

  const [searchTerm, setSearchTerm] = useState(searchFromUrl);
  const [pagination, setPagination] = useState({
    current_page: pageFromUrl,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  // ✅ حالة الأزرار الشاملة - تهيئة بقيم false
  const [allStatuses, setAllStatuses] = useState({
    is_booking_approved: false,
    is_fast_booking_approved: false,
    is_transport_approved: false,
    is_special_booking_approved: false,
    updated_count: 0,
  });

  const [togglingAll, setTogglingAll] = useState(false);

  const location = useLocation();
  const highlightedUserId = location.state?.scrollToUser;

  // ============================================
  // ✅ دالة جلب الحالة العامة للسائقين من API
  // ============================================
  const fetchAllDriversStatus = async () => {
    try {
      const response = await driversAPI.getAllDriversStatus();
      if (response.success && response.data) {
        setAllStatuses({
          is_booking_approved: response.data.is_booking_approved ?? false,
          is_fast_booking_approved: response.data.is_fast_booking_approved ?? false,
          is_transport_approved: response.data.is_transport_approved ?? false,
          is_special_booking_approved: response.data.is_special_booking_approved ?? false,
          updated_count: response.data.updated_count || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching all drivers status:", err);
    }
  };

  // ============================================
  // ✅ دالة حساب الحالة من بيانات السائقين (بديل إذا لم يكن هناك API)
  // ============================================
  const calculateAllStatusesFromData = (driversData) => {
    if (!driversData || driversData.length === 0) {
      setAllStatuses({
        is_booking_approved: false,
        is_fast_booking_approved: false,
        is_transport_approved: false,
        is_special_booking_approved: false,
        updated_count: 0,
      });
      return;
    }

    const allBookingApproved = driversData.every(driver => driver.is_booking_approved === true);
    const allFastBookingApproved = driversData.every(driver => driver.is_fast_booking_approved === true);
    const allTransportApproved = driversData.every(driver => driver.is_transport_approved === true);
    const allSpecialBookingApproved = driversData.every(driver => driver.is_special_booking_approved === true);

    setAllStatuses({
      is_booking_approved: allBookingApproved,
      is_fast_booking_approved: allFastBookingApproved,
      is_transport_approved: allTransportApproved,
      is_special_booking_approved: allSpecialBookingApproved,
      updated_count: driversData.length,
    });
  };

  // Fetch drivers with pagination and search
  const fetchDrivers = async (page = 1, search = searchTerm) => {
    try {
      setLoading(true);
      setError('');
      const response = await driversAPI.index({
        page,
        per_page: pagination.per_page,
        search,
      });

      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        
        // ✅ حساب الحالة من البيانات المجلوبة
        calculateAllStatusesFromData(fetchedData);
        
        setPagination(response.data.meta || {
          current_page: page,
          last_page: response.data.last_page || 1,
          per_page: response.data.per_page || 10,
          total: response.data.total || 0,
        });
      } else {
        setError(response.message || t('failed_to_fetch_drivers'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_drivers'));
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ✅ جميع الـ useEffect
  // ============================================
  
  // جلب البيانات الأولية
  useEffect(() => {
    fetchDrivers(pageFromUrl, searchFromUrl);
    // ✅ جلب الحالة العامة من API (إذا كان متاحاً)
    fetchAllDriversStatus();
  }, []);

  // تحديث البيانات عند تغيير URL
  useEffect(() => {
    fetchDrivers(pageFromUrl, searchFromUrl);
  }, [pageFromUrl, searchFromUrl]);

  // Debounced Search Effect to update URL
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== searchFromUrl) {
        setSearchParams({ page: 1, search: searchTerm });
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  // التمرير إلى المستخدم المميز
  useEffect(() => {
    if (highlightedUserId) {
      const element = document.getElementById(`user-${highlightedUserId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [data]);

  // ============================================
  // ✅ دوال التحكم
  // ============================================

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await driversAPI.delete(deleteId);
      if (response.success) {
        setShowDelete(false);
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_delete_driver'));
      }
    } catch (err) {
      setError(t('failed_to_delete_driver'));
      console.error("Error deleting driver:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await driversAPI.toggleStatus(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
      console.error("Error toggling driver status:", err);
    }
  };

  const toggleEmailChecked = async (id) => {
    try {
      const response = await driversAPI.emailChecked(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_email'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_email'));
      console.error("Error toggling email:", err);
    }
  };

  const toggleSmsChecked = async (id) => {
    try {
      const response = await driversAPI.smsChecked(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_sms'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_sms'));
      console.error("Error toggling SMS:", err);
    }
  };

  // ============================================
  // ✅ دوال التفعيل الشامل
  // ============================================

  const toggleAllBookingStatus = async () => {
    try {
      setTogglingAll(true);
      const response = await driversAPI.toggleAllBookingApproved();
      if (response.success) {
        const newStatus = response.data.new_status ?? true;
        const updatedCount = response.data.updated_count || 0;
        
        setAllStatuses(prev => ({
          ...prev,
          is_booking_approved: newStatus,
          updated_count: updatedCount,
        }));
        
        await fetchDrivers(pagination.current_page);
        
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: response.data.message || `${updatedCount} ${t("drivers_updated")}`,
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        setError(response.message || t("failed_to_toggle_status"));
      }
    } catch (err) {
      setError(t("failed_to_toggle_status"));
    } finally {
      setTogglingAll(false);
    }
  };

  const toggleAllFastBookingStatus = async () => {
    try {
      setTogglingAll(true);
      const response = await driversAPI.toggleAllFastBookingApproved();
      if (response.success) {
        const newStatus = response.data.new_status ?? true;
        const updatedCount = response.data.updated_count || 0;
        
        setAllStatuses(prev => ({
          ...prev,
          is_fast_booking_approved: newStatus,
          updated_count: updatedCount,
        }));
        
        await fetchDrivers(pagination.current_page);
        
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: response.data.message || `${updatedCount} ${t("drivers_updated")}`,
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        setError(response.message || t("failed_to_toggle_status"));
      }
    } catch (err) {
      setError(t("failed_to_toggle_status"));
    } finally {
      setTogglingAll(false);
    }
  };

  const toggleAllTransportStatus = async () => {
    try {
      setTogglingAll(true);
      const response = await driversAPI.toggleAllTransportApproved();
      if (response.success) {
        const newStatus = response.data.new_status ?? true;
        const updatedCount = response.data.updated_count || 0;
        
        setAllStatuses(prev => ({
          ...prev,
          is_transport_approved: newStatus,
          updated_count: updatedCount,
        }));
        
        await fetchDrivers(pagination.current_page);
        
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: response.data.message || `${updatedCount} ${t("drivers_updated")}`,
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        setError(response.message || t("failed_to_toggle_status"));
      }
    } catch (err) {
      setError(t("failed_to_toggle_status"));
    } finally {
      setTogglingAll(false);
    }
  };

  const toggleAllSpecialBookingStatus = async () => {
    try {
      setTogglingAll(true);
      const response = await driversAPI.toggleAllSpecialBookingApproved();
      if (response.success) {
        const newStatus = response.data.new_status ?? true;
        const updatedCount = response.data.updated_count || 0;
        
        setAllStatuses(prev => ({
          ...prev,
          is_special_booking_approved: newStatus,
          updated_count: updatedCount,
        }));
        
        await fetchDrivers(pagination.current_page);
        
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: response.data.message || `${updatedCount} ${t("drivers_updated")}`,
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        setError(response.message || t("failed_to_toggle_status"));
      }
    } catch (err) {
      setError(t("failed_to_toggle_status"));
    } finally {
      setTogglingAll(false);
    }
  };

  // ============================================
  // ✅ دوال التفعيل الفردي
  // ============================================

  const toggleBookingStatus = async (id) => {
    try {
      const response = await driversAPI.toggleBookingApproved(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
    }
  };

  const toggleFastBookingStatus = async (id) => {
    try {
      const response = await driversAPI.toggleFastBookingApproved(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
    }
  };

  const toggleTransportStatus = async (id) => {
    try {
      const response = await driversAPI.toggleTransportApproved(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
    }
  };

  const toggleSpecialBookingStatus = async (id) => {
    try {
      const response = await driversAPI.toggleSpecialBookingApproved(id);
      if (response.success) {
        fetchDrivers(pagination.current_page);
      } else {
        setError(response.message || t('failed_to_toggle_status'));
      }
    } catch (err) {
      setError(t('failed_to_toggle_status'));
    }
  };

  // ============================================
  // ✅ دالة البحث
  // ============================================
  const clearSearch = () => {
    setSearchTerm('');
    setSearchParams({ page: 1, search: '' });
  };

  // ============================================
  // ✅ Render الشرطي (بعد كل الـ Hooks)
  // ============================================
  if (loading && data.length === 0) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  // ============================================
  // ✅ الـ Render الرئيسي
  // ============================================
  return (
    <>
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
          <h3 className="fw-bold mb-0">👤 {t('drivers_list')}</h3>
          <div className='d-flex gap-3'>
            <Button 
              className="btn btn-success" 
              onClick={() => navigate('/admin/drivers/drivers-approved')}
            >
              {t('drivers_approved')}
            </Button>
            <Button 
              className="btn btn-warning" 
              onClick={() => navigate('/admin/drivers/drivers-pending')}
            >
              {t('drivers_pending')}
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('search_by_email')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title={t("reset_search")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>

        {/* ============================================
            ALL DRIVERS STATUS CONTROLS
        ============================================ */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <div>
              <div className="mb-3">
                <h6 className="fw-bold mb-1">
                  <FaUsers className="me-2 text-primary" />
                  {t("all_drivers_status")}
                </h6>
                <small className="text-muted">
                  {t("toggle_all_drivers_status_description")}
                </small>
              </div>
              <div className="d-flex flex-wrap gap-3">
                {/* زر تفعيل جميع طلبات النقل العادية */}
                <div className="approve-div d-flex align-items-center gap-2">
                  <span className="small text-muted">
                    {t("booking_approved")}
                  </span>
                  <Form.Check
                    type="switch"
                    id="all-booking-approved"
                    checked={allStatuses.is_booking_approved || false}
                    onChange={toggleAllBookingStatus}
                    disabled={togglingAll}
                    className="custom-switch"
                  />
                  <Badge 
                    bg={allStatuses.is_booking_approved ? "success" : "secondary"}
                    className="ms-1"
                  >
                    {allStatuses.is_booking_approved ? "✓" : "✗"}
                  </Badge>
                </div>

                {/* زر تفعيل جميع طلبات Fast */}
                <div className="approve-div d-flex align-items-center gap-2">
                  <span className="small text-muted">
                    {t("fast_booking_approved")}
                  </span>
                  <Form.Check
                    type="switch"
                    id="all-fast-booking-approved"
                    checked={allStatuses.is_fast_booking_approved || false}
                    onChange={toggleAllFastBookingStatus}
                    disabled={togglingAll}
                    className="custom-switch"
                  />
                  <Badge 
                    bg={allStatuses.is_fast_booking_approved ? "success" : "secondary"}
                    className="ms-1"
                  >
                    {allStatuses.is_fast_booking_approved ? "✓" : "✗"}
                  </Badge>
                </div>

                {/* زر تفعيل جميع طلبات Transport */}
                <div className="approve-div d-flex align-items-center gap-2">
                  <span className="small text-muted">
                    {t("transport_approved")}
                  </span>
                  <Form.Check
                    type="switch"
                    id="all-transport-approved"
                    checked={allStatuses.is_transport_approved || false}
                    onChange={toggleAllTransportStatus}
                    disabled={togglingAll}
                    className="custom-switch"
                  />
                  <Badge 
                    bg={allStatuses.is_transport_approved ? "success" : "secondary"}
                    className="ms-1"
                  >
                    {allStatuses.is_transport_approved ? "✓" : "✗"}
                  </Badge>
                </div>

                {/* زر تفعيل جميع طلبات Special Booking */}
                <div className="approve-div d-flex align-items-center gap-2">
                  <span className="small text-muted">
                    {t("special_booking_approved")}
                  </span>
                  <Form.Check
                    type="switch"
                    id="all-special-booking-approved"
                    checked={allStatuses.is_special_booking_approved || false}
                    onChange={toggleAllSpecialBookingStatus}
                    disabled={togglingAll}
                    className="custom-switch"
                  />
                  <Badge 
                    bg={allStatuses.is_special_booking_approved ? "success" : "secondary"}
                    className="ms-1"
                  >
                    {allStatuses.is_special_booking_approved ? "✓" : "✗"}
                  </Badge>
                </div>
              </div>
            </div>
            {togglingAll && (
              <div className="mt-2">
                <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                <span className="text-muted small">{t("updating_all_drivers")}</span>
              </div>
            )}
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        <div className="table-container shadow-sm rounded">
          <div className='table-responsive'>
            <Table bordered hover className="custom-table mb-0 text-center">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>{t('first_name')}</th>
                  <th>{t('company_name')}</th>
                  <th>{t('last_name')}</th>
                  <th>{t('email')}</th>
                  <th>{t('phone')}</th>
                  <th>{t('rating')}</th>
                  <th>{t('status')}</th>
                  <th>{t('mail')}</th>
                  <th>{t('sms')}</th>
                  <th>{t('approved')}</th>
                  <th>{t('booking_approved')}</th>
                  <th>{t('fast_booking_approved')}</th>
                  <th>{t('transport_approved')}</th>
                  <th>{t('special_booking_approved')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((driver, index) => (
                  <tr key={driver.id} id={`user-${driver.id}`} className={driver.id === highlightedUserId ? "user-highlight" : ""}>
                    <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                    <td>{driver.firstname || '-'}</td>
                    <td>{driver.company_name || '-'}</td>
                    <td>{driver.lastname || '-'}</td>
                    <td>
                      {searchTerm ? (
                        <span 
                          dangerouslySetInnerHTML={{
                            __html: driver.email?.replace(
                              new RegExp(searchTerm, 'gi'), 
                              match => `<mark class="bg-warning">${match}</mark>`
                            )
                          }} 
                        />
                      ) : (
                        driver.email
                      )}
                    </td>
                    <td>{driver.telphone || '-'}</td>
                    <td>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`fs-4 ${star <= Math.round(driver.average_rating || 0) ? 'text-warning' : 'text-muted'}`}
                        >
                          ★
                        </span>
                      ))}
                    </td>
                    <td>
                      <Badge bg={driver.status === 'Approved' ? 'success' : 'danger'}>
                        {driver.status === 'Approved' ? t('active') : t('inactive')}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center">
                        <Form.Check
                          type="switch"
                          id={`mail-${driver.id}`}
                          checked={driver.email_checked}
                          onChange={() => toggleEmailChecked(driver.id)}
                          className="custom-switch"
                        />
                        <span className="toggle-label">
                          {driver.email_checked ? t('on') : t('off')}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center">
                        <Form.Check
                          type="switch"
                          id={`sms-${driver.id}`}
                          checked={driver.sms_checked}
                          onChange={() => toggleSmsChecked(driver.id)}
                          className="custom-switch"
                        />
                        <span className="toggle-label">
                          {driver.sms_checked ? t('on') : t('off')}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center">
                        <Form.Check
                          type="switch"
                          id={`approved-${driver.id}`}
                          checked={driver.admin_approve === "2" || driver.admin_approve === 2}
                          onChange={() => toggleStatus(driver.id)}
                          className="custom-switch"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center">
                        <Form.Check
                          type="switch"
                          id={`booking-approved-${driver.id}`}
                          checked={!!driver.is_booking_approved}
                          onChange={() => toggleBookingStatus(driver.id)}
                          className="custom-switch"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center">
                        <Form.Check
                          type="switch"
                          id={`fast-booking-approved-${driver.id}`}
                          checked={!!driver.is_fast_booking_approved}
                          onChange={() => toggleFastBookingStatus(driver.id)}
                          className="custom-switch"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center">
                        <Form.Check
                          type="switch"
                          id={`transport-approved-${driver.id}`}
                          checked={!!driver.is_transport_approved}
                          onChange={() => toggleTransportStatus(driver.id)}
                          className="custom-switch"
                        />
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center justify-content-center">
                        <Form.Check
                          type="switch"
                          id={`special-booking-approved-${driver.id}`}
                          checked={!!driver.is_special_booking_approved}
                          onChange={() => toggleSpecialBookingStatus(driver.id)}
                          className="custom-switch"
                        />
                      </div>
                    </td>
                    <td>
                      <Button
                        variant="outline-info"
                        className="me-2"
                        onClick={() => navigate(`/admin/drivers/details/${driver.id}`, { 
                          state: { 
                            fromPage: pagination.current_page, 
                            userId: driver.id,
                            search: searchTerm,
                            fromPath: location.pathname
                          } 
                        })}
                        title={t('view_details')}
                      >
                        <FaInfoCircle />
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => {
                          setDeleteId(driver.id);
                          setShowDelete(true);
                        }}
                        title={t('delete')}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>

        {pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={(newPage) => setSearchParams({ page: newPage, search: searchTerm })}
            disabled={loading}
          />
        )}

        {/* Delete Modal */}
        <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('confirm_deletion')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{t('confirm_delete_driver')}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDelete(false)}>
              {t('cancel')}
            </Button>
            <Button variant="danger" onClick={confirmDelete} disabled={loading}>
              {loading ? t('deleting') : t('delete')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default Drivers;